import { Add, Check } from '@mui/icons-material';
import { Box, Button, Card, CardActions, CardContent, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentPlan, getRequestedPlan } from 'redux/reducers/pricing/selectors';
import { requestNewPlanRequest } from 'redux/reducers/pricing/reducer';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { TPricingCardBigProps } from './types';
import { RequestChangeDialog } from '../RequestChangeDialog';
import { DATE_STOP_CHANGE_REQUEST } from '../constants';

export const PricingCardBig: FC<TPricingCardBigProps> = ({ data }) => {
	const dispatch = useDispatch();
	const currentPlan = useSelector(getCurrentPlan);
	const requestedPlan = useSelector(getRequestedPlan);
	const now = DateTime.now();
	const value = (+(data?.monthly_fee_value ?? 0)).toFixed(2).split('.')[0];
	const coins = (+(data?.monthly_fee_value ?? 0)).toFixed(2).split('.')[1];

	const items = () => {
		const i = [];
		if (data.ioss_uk_monthly_free) i.push({ type: 'free', name: data.ioss_uk_monthly_free });
		if (data.oss_monthly_free) i.push({ type: 'free', name: data.oss_monthly_free });
		if (data.ioss_uk_monthly_add)
			i.push({ type: 'add', name: data.ioss_uk_monthly_add, value: data.IOSS });
		if (data.oss_monthly_add) i.push({ type: 'add', name: data.oss_monthly_add, value: data.OSS });
		if (data.non_union_monthly_add)
			i.push({ type: 'add', name: data.non_union_monthly_add, value: data.NON_UNION });

		return i;
	};

	const [openDialog, setOpenDialog] = useState(false);

	const handleRequestNewPlan = () => {
		dispatch(
			requestNewPlanRequest({ newMonthlyFeeTypeId: data.id, newFeeClassId: data.newfeeclassid }),
		);
		setOpenDialog(false);
	};

	const [openBookMeetingDialog, setOpenBookMeetingDialog] = useState(false);

	const handleBookMeeting = () => {
		window.open('https://easproject.com/schedule-a-meeting-with-eas/', '_blank');
		setOpenBookMeetingDialog(false);
	};

	return (
		<Card
			sx={{
				width: '100%',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'space-between',
				alignItems: 'center',
				m: 0,
				p: 0,
			}}
		>
			<CardContent
				sx={{
					m: 0,
					p: 0,
					width: '100%',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'start',
						alignItems: 'space-between',
						gap: 0,
						opacity:
							data.available_for_customer ||
							data.id === currentPlan?.id ||
							data.id === requestedPlan?.newMonthlyFeeTypeId
								? 1
								: 0.7,
					}}
				>
					<Box
						color="primary"
						sx={{
							backgroundColor: 'primary.main',

							color: 'white',
							height: '53px',
							width: '360px',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'start',
							justifyContent: 'space-between',
							p: '4px 8px',
						}}
					>
						<Typography
							component="div"
							sx={{
								fontSize: '1rem',
								fontWeight: 'bold',
								m: 0,
								p: 0,
							}}
						>
							{data.transaction_plan ? 'Transaction based pricing' : data.name_for_invoice}
						</Typography>
						<Typography
							sx={{
								fontSize: '0.7rem',
								lineHeight: '1.1',
							}}
						>
							{data.dashboard_description ?? <span>&nbsp;</span>}
						</Typography>
					</Box>
					{!data.transaction_plan && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'top',
								alignItems: 'center',
								p: '5px',
								backgroundColor: '#01619336',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'center',
									alignItems: 'top',
									mt: '-5px',
									p: '0 4px 0 0',
								}}
							>
								<Typography
									component="span"
									color="primary"
									sx={{
										fontSize: '0.9rem',
										fontWeight: 'bold',
										mt: '9px',
										mr: '2px',
										p: 0,
										color: 'primary.main',
									}}
								>
									€
								</Typography>
								<Typography
									component="div"
									color="primary"
									sx={{
										fontSize: '2.2rem',
										fontWeight: 'bold',
										m: 0,
										p: 0,
										color: 'primary.main',
									}}
								>
									{value}
								</Typography>
								<Typography
									component="span"
									color="primary"
									sx={{
										fontSize: '0.9rem',
										fontWeight: 'bold',
										mt: '9px',
										mr: '2px',
										p: 0,
										color: 'primary.main',
									}}
								>
									{coins}
								</Typography>
							</Box>
							<Typography
								sx={{
									fontSize: '0.7rem',
									mt: '-15px',
									p: 0,
									color: 'primary.main',
								}}
								color="primary"
							>
								month
							</Typography>
						</Box>
					)}
				</Box>
				<Box>
					{items().map((i) => (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								alignItems: 'start',
								p: '4px',
							}}
						>
							{i.type === 'free' ? (
								<Check color="success" fontSize="small" />
							) : (
								<Add color="primary" fontSize="small" />
							)}
							<Typography
								color={i.type === 'free' ? 'success.main' : 'primary'}
								sx={{
									ml: '5px',
									color: i.type === 'free' ? 'success.main' : 'primary.main',
									fontSize: '0.9rem',
								}}
							>
								{i.value ? `${i.value} ` : ''}
								{i.name}
							</Typography>
						</Box>
					))}
				</Box>
			</CardContent>
			<CardActions
				sx={{
					width: 'calc(100% - 20px)',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					m: 0,
					p: 0,
				}}
			>
				{data.id === currentPlan?.id ? (
					<Typography
						sx={{
							fontSize: '1rem',
							fontWeight: 'bold',
							mb: '10px',
							p: '10px',
							color: 'success.main',
						}}
					>
						Current Plan
					</Typography>
				) : data.id === requestedPlan?.newMonthlyFeeTypeId ? (
					<Typography
						sx={{
							fontSize: '1rem',
							fontWeight: 'bold',
							mb: '10px',
							p: '10px',
							color: 'warning.main',
						}}
					>
						Requested Plan
					</Typography>
				) : data.available_for_customer ? (
					<Button
						color="primary"
						size="small"
						sx={{
							textTransform: 'none',
							borderRadius: '12px',
							fontWeight: 'bold',
							m: '10px',
						}}
						variant="contained"
						fullWidth
						onClick={() => setOpenDialog(true)}
						disabled={now.day >= DATE_STOP_CHANGE_REQUEST}
					>
						Request Change
					</Button>
				) : (
					<Button
						color="primary"
						size="small"
						sx={{
							textTransform: 'none',
							borderRadius: '12px',
							fontWeight: 'bold',
							m: '10px',
						}}
						variant="contained"
						fullWidth
						onClick={() => setOpenBookMeetingDialog(true)}
					>
						Book a meeting
					</Button>
				)}
			</CardActions>
			<RequestChangeDialog
				open={openDialog}
				setClose={() => setOpenDialog(false)}
				handleClick={handleRequestNewPlan}
				text="Are you sure you want to request a change to this plan?"
			/>
			<ConfirmationDialog
				title="Book a meeting"
				open={openBookMeetingDialog}
				setClose={() => setOpenBookMeetingDialog(false)}
				handleConfirm={handleBookMeeting}
				text="The subscription plan is incompatible with your account's existing configuration. Please contact us to find suitable solution for you."
				confirmButtonText="Book a meeting"
			/>
		</Card>
	);
};
